import commonActions from '@/store/data/common/actions'
import { webRequestClient } from '@/services'
import { errorHandler } from '@/utils/helpers'
import { isCsvFile, parseGeoradarCSV, unparseGeoradarCSV, mergePoints, truncateCoordinate, instanciateAll } from './helpers'
import { head, toNumber } from 'lodash'

const actions = {
    /**
     * Retrieves the list of georadar points, and set them into the list of saved points
     * @returns {Promise}
     */
    retrieveGeoradarPoints({ commit, rootState }) {
        commit('setRetrievePointsLoading', true)
        return webRequestClient
            .get('/audience_admin/georadars.json', { params: { account_id: rootState.account.accountId, list_limit: 0 } })
            .then(response => {
                commit('initializePoints', { points: response.list })
                commit('setCollection', instanciateAll(response.list))
            })
            .catch(error => {
                errorHandler(error, commit)
                // throw error
            })
            .finally(() => {
                commit('setRetrievePointsLoading', false)
            })
    },

    async importGeoradarCSV({ commit, dispatch }, { file }) {
        commit('setCheckIsLoading', true)

        try {
            if (!isCsvFile(file)) throw 'Your file have to be a CSV file'
            const res = await parseGeoradarCSV(file)
            const points = res.map(p => ({
                ...p,
                action: 'create',
                latitude: truncateCoordinate(p.latitude),
                longitude: truncateCoordinate(p.longitude)
            }))
            await dispatch('addImportedPoints', { points })
        } catch (err) {
            errorHandler(err, commit)
        } finally {
            commit('setCheckIsLoading', false)
        }
    },

    getSavedPointsToCSV({ state }) {
        const points = state.savedPoints.map(p => ({
            label: p.label,
            latitude: p.latitude,
            longitude: p.longitude,
            radius: p.radius
        }))
        return points.length > 0 ? unparseGeoradarCSV(JSON.stringify(points)) : 'label;latitude;longitude;radius'
    },

    /**
     * Checks if sended point match API requirements and add It in the store
     * @param {Object} payload
     * @param {Object} payload.point point to check
     * @returns {Promise}
     */
    async addNewPoint({ commit }, { point }) {
        try {
            const params = { points: JSON.stringify([point]) }
            const newPoints = await webRequestClient.post('/audience_admin/georadars/check/json', params)
            commit('addNewPoints', { points: newPoints })
        } catch (error) {
            if (error.apiError?.status === 400) {
                error.apiError.message.forEach(point => {
                    if (point.errors) {
                        Object.values(point.errors).forEach(errors => {
                            setTimeout(() => {
                                errorHandler(errors.join(', '), commit)
                            }, 1)
                        })
                    }
                })
            } else {
                errorHandler(error.responseErrorMessage, commit)
                throw error
            }
        }
    },

    /**
     *
     * @param {Object} payload
     * @param {Object[]} payload.points points to check
     */
    async addImportedPoints({ commit }, { points }) {
        try {
            const newPoints = await webRequestClient.post('/audience_admin/georadars/check/json', { points: JSON.stringify(points) })
            commit('addNewPoints', { points: newPoints })
        } catch (error) {
            if (error.apiError?.status === 400) {
                commit('addNewPoints', { points: error.apiError.message })
                commit(
                    'notifications/setAddedNotification',
                    {
                        type: 'warning',
                        message: 'Some of your imported points have errors. Find them in red in the list. Please correct them before saving them.'
                    },
                    { root: true }
                )
            } else {
                errorHandler(error.responseErrorMessage, commit)
                throw error
            }
        }
    },

    /**
     *
     * @param {Object} payload
     * @param {Number} payload.index index of the points
     * @param {Object} payload.point
     */
    async updateGeoradarPoint({ commit }, { index, point }) {
        try {
            const params = { points: JSON.stringify([point]) }
            const newPoints = await webRequestClient.post('/audience_admin/georadars/check/json', params)
            commit('setPoint', { index, value: { ...head(newPoints) } })
        } catch (error) {
            if (error.apiError?.status === 400) {
                error.apiError.message.forEach(point => {
                    if (point.errors) {
                        Object.values(point.errors).forEach(errors => {
                            setTimeout(() => {
                                errorHandler(errors.join(', '), commit)
                            }, 1)
                        })
                    }
                })
            } else {
                errorHandler(error.responseErrorMessage, commit)
                throw error
            }
        }
    },

    /**
     * Save changes on points, can be create / update / delete in same request
     * @param {Object[]} payload.points points to save
     */
    async saveGeoradarPoints({ commit, rootState }, { points }) {
        const params = {
            account_id: toNumber(rootState.account.accountId),
            points: JSON.stringify(points)
        }

        try {
            const res = await webRequestClient.post('/audience_admin/georadars/import.json', params)
            mergePoints(commit, { source: 'savedPoints', points: res })
            const deletedPoints = res.filter(point => point.action === 'delete')
            const updatedPoints = instanciateAll([...res.filter(point => point.action !== 'delete')])

            updatedPoints.forEach(point => {
                commit('setGeoradarInCollection', point)
            })
            deletedPoints.forEach(point => {
                commit('removeAudienceInCollection', 'Georadar_' + point.id)
            })

            commit('syncPoints')
            commit(
                'notifications/setAddedNotification',
                {
                    type: 'success',
                    message: 'Your georadar points have been saved'
                },
                { root: true }
            )
        } catch (error) {
            if (error.apiError?.status === 400) {
                const savedPoints = error.apiError.message.savedPoints
                const unSavedPoints = error.apiError.message.unSavedPoints
                if (savedPoints.length > 0) {
                    mergePoints(commit, { source: 'savedPoints', points: savedPoints })
                    commit('syncPoints')
                    mergePoints(commit, { source: 'unSavedPoints', points: unSavedPoints })
                    commit(
                        'notifications/setAddedNotification',
                        {
                            type: 'warning',
                            message: `
                                ${unSavedPoints.length} of your points have error(s). Please correct them before trying to save them again. 
                                ${savedPoints.length} valid point${savedPoints.length > 1 ? 's' : ''} have been saved.`
                        },
                        { root: true }
                    )
                } else {
                    errorHandler(
                        `your point${unSavedPoints.length > 1 ? 's' : ''} have error(s). Please correct them before trying to save them again.`,
                        commit
                    )
                }
            } else {
                errorHandler(error.responseErrorMessage, commit)
            }
        }
    }
}

export default { ...commonActions, ...actions }
